import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import BaseModel from '../../BaseModal';
import { BaseButton } from '../../styles/ButtonStyles';
import { authorizedHttpGet, authorizedHttpPost } from '../../api/httpCalls';
import urls from '../../api/urls.json';
import { GapView } from '../../styles/WrapperStyles';

const ParentDiv = styled.div`
  width: 460px;
  max-width: 90vw;
  padding: 12px;
`;

const Title = styled.div`
  font-family: 'Gilory-Semi';
  font-size: 24px;
  font-weight: 800;
  color: #1f1f1f;
`;

const Label = styled.div`
  margin-top: 14px;
  font-family: 'Gilory-Semi';
  font-size: 16px;
  font-weight: 700;
  color: #22313a;
`;

const Input = styled.input`
  width: 100%;
  margin-top: 8px;
  padding: 12px 14px;
  border: 1px solid #ccd4da;
  border-radius: 12px;
  font-size: 16px;
  box-sizing: border-box;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  margin-top: 8px;
  padding: 12px 14px;
  border: 1px solid #ccd4da;
  border-radius: 12px;
  font-size: 16px;
  resize: vertical;
  box-sizing: border-box;
`;

const MetaText = styled.div`
  margin-top: 8px;
  font-family: 'Gilory-Semi';
  font-size: 14px;
  color: #55646d;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const formatDateForApi = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString();
};

const ExtendSubscriptionModal = ({ closeModal, userRef, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('');
  const [activeSubscription, setActiveSubscription] = useState(null);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      setLoading(true);
      const response = await authorizedHttpGet(`${urls.LIST_SUBSCRIPTIONS}?ref=${userRef}`);
      if (response.status) {
        const activeSub = (response.data.subscriptions || []).find((sub) => sub.is_active);
        setActiveSubscription(activeSub || null);
      }
      setLoading(false);
    };

    fetchSubscriptions();
  }, [userRef]);

  const handleSave = async () => {
    const parsedDays = Number(days);

    if (!activeSubscription) {
      onError('No active subscription found');
      return;
    }

    if (!Number.isInteger(parsedDays) || parsedDays <= 0) {
      onError('Please enter a valid number of days');
      return;
    }

    if (!reason.trim()) {
      onError('Please enter a reason');
      return;
    }

    const currentEndDate = activeSubscription.subscription_end_time
      ? new Date(activeSubscription.subscription_end_time)
      : new Date();

    if (Number.isNaN(currentEndDate.getTime())) {
      onError('Invalid current subscription end date');
      return;
    }

    const updatedEndDate = new Date(currentEndDate);
    updatedEndDate.setDate(updatedEndDate.getDate() + parsedDays);

    setLoading(true);
    const response = await authorizedHttpPost(urls.UPDATE_SUBSCRIPTION_END_DATE, {
      id: activeSubscription.id,
      end_date: formatDateForApi(updatedEndDate),
      reason: reason.trim()
    });
    setLoading(false);

    if (response.status) {
      closeModal();
      onSuccess(`Subscription extended by ${parsedDays} day${parsedDays > 1 ? 's' : ''}`);
      return;
    }

    onError(response.data?.error || 'Unable to extend subscription');
  };

  return (
    <BaseModel closeModal={closeModal}>
      <ParentDiv>
        <Title>Extend Subscription Days</Title>
        <GapView height="10px" />

        {loading ? (
          <MetaText>Loading...</MetaText>
        ) : (
          <>
            <MetaText>
              Current end date: {formatDateForDisplay(activeSubscription?.subscription_end_time)}
            </MetaText>

            <Label>Number of days</Label>
            <Input
              type="number"
              min="1"
              value={days}
              onChange={(event) => setDays(event.target.value)}
              placeholder="Enter number of days"
            />

            <Label>Reason</Label>
            <TextArea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Enter reason for adding extra subscription days"
            />

            <ButtonRow>
              <BaseButton onClick={handleSave}>Save</BaseButton>
              <BaseButton onClick={closeModal}>Cancel</BaseButton>
            </ButtonRow>
          </>
        )}
      </ParentDiv>
    </BaseModel>
  );
};

export default ExtendSubscriptionModal;
